import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Barcode, UserPlus } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [inv, setInv] = useState(null);
  const [err, setErr] = useState(null);
  const [form, setForm] = useState({ name: '', password: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/api/auth/invites/${token}`)
      .then((r) => { setInv(r.data); setForm((f) => ({ ...f, name: r.data.name || '' })); })
      .catch((e) => setErr(e?.response?.data?.detail || 'Invite invalid or expired'));
  }, [token]);

  const accept = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    if (form.password.length < 8) { toast.error('Password must be 8+ chars'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/auth/invites/accept`,
        { token, name: form.name, password: form.password },
        { withCredentials: true });
      await checkAuth();
      toast.success(`Welcome to ${inv.company.name}!`);
      navigate('/', { replace: true });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to accept');
      setBusy(false);
    }
  };

  if (err) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="invite-error-page">
        <div className="bg-card border border-border rounded-lg p-8 max-w-md w-full text-center">
          <h1 className="text-2xl font-bold mb-2">Invite unavailable</h1>
          <p className="text-muted-foreground mb-6 text-sm">{err}</p>
          <Link to="/login" className="text-brand font-bold text-sm uppercase tracking-widest">← Back to Sign in</Link>
        </div>
      </div>
    );
  }
  if (!inv) return <div className="min-h-screen flex items-center justify-center">Loading…</div>;

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="invite-accept-page">
      <div className="bg-card border border-border rounded-lg p-8 max-w-md w-full">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-brand flex items-center justify-center">
            <Barcode weight="bold" className="text-white" size={22} />
          </div>
          <span className="font-bold text-xl tracking-tight">STOCKARIS</span>
        </div>
        <div className="mb-6">
          <div className="inline-block text-[10px] uppercase tracking-widest text-brand bg-brand/10 font-bold px-2 py-0.5 mb-2">
            <UserPlus size={10} weight="fill" className="inline mr-1" />Team invite
          </div>
          <h1 className="text-2xl font-bold">Join {inv.company.name}</h1>
          <p className="text-sm text-muted-foreground mt-1">Signing up as <b className="font-mono uppercase">{inv.email}</b> · role: <b className="font-mono uppercase">{inv.role}</b></p>
        </div>
        <div className="space-y-4">
          <div>
            <Label className="overline">Your name</Label>
            <Input data-testid="invite-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label className="overline">Password</Label>
            <Input data-testid="invite-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 8 characters" />
          </div>
          <Button data-testid="invite-accept-btn" onClick={accept} disabled={busy} className="w-full bg-brand hover:bg-brand/90 text-white rounded-none font-bold py-3">
            {busy ? 'Creating account…' : 'Accept & Join'}
          </Button>
        </div>
      </div>
    </div>
  );
}
